import { attributesToProps, domToReact, type HTMLReactParserOptions } from 'html-react-parser';
import { createElement, type JSX, type ReactNode } from 'react';
import { Icon } from '@/app_components/common/Icon';
import { parseBaseComponents, renderChild } from '@/app_lib/htmlParser/parsers/parserOptions';
import { type DOMNode, isElement, type ParserProps } from '@/app_lib/htmlParser/types';
import { cn } from '@/app_lib/utils/utils';

function transformNode(domNode: DOMNode, reactNode: ReactNode, index: number, tag: string): ReactNode {
  if (!isElement(domNode)) {
    return reactNode;
  }

  const baseParsedResults = parseBaseComponents(domNode, reactNode, index, tag);
  if (baseParsedResults) {
    return baseParsedResults;
  }

  return reactNode;
}

function findHtag(className?: string): string {
  const validHeadings = new Set(['h2', 'h3', 'h4', 'h5', 'h6']);
  const classList = className?.split(' ') || [];
  const found = classList.find((cls) => validHeadings.has(cls));
  return found ?? 'h3';
}

export const DetailParser = ({ domNode: dNode }: ParserProps) => {
  const { attribs, children } = dNode;
  const props = attributesToProps(attribs);
  const { className, ...rest } = props;

  const options: HTMLReactParserOptions = {
    transform(reactNode: ReactNode, domNode: DOMNode, index: number) {
      //  raw-html-parser types are not up to date
      return transformNode(domNode, reactNode, index, findHtag(className?.toString())) as JSX.Element;
    },
  };

  return (
    <details
      className={cn(
        'group open:flex flex-col gap-sm prose-p:typography-default prose-p:font-inria-serif prose-ul:typography-default prose-ul:font-inria-serif prose-strong:text-primary',
        className,
      )}
      {...rest}
    >
      {domToReact(children as DOMNode[], options)}
    </details>
  );
};

export const SummaryParser = ({ domNode, tag = 'h3' }: ParserProps & { tag?: string }) => {
  const { attribs, children } = domNode;
  const props = attributesToProps(attribs);
  const { className, ...rest } = props;
  return (
    <summary
      className={cn(
        'prose cursor-pointer text-primary list-none stroke-secondary border-b [&_*]:m-0 [&_a]:text-accent pb-1 font-graphik-compact font-semibold text-primary-hover **:text-primary-hover group-focus-within:text-accent md:group-focus-within:stroke-accent group-open:stroke-accent **:no-underline!',
        className,
      )}
      {...rest}
    >
      <span className='flex items-center justify-between h-fit'>
        {createElement(tag, { className: 'wp-block-heading' }, renderChild(children))}
        <Icon
          className='transition-transform group-open:rotate-180 group-hover:stroke-accent'
          icon='ChevronDown'
        />
      </span>
    </summary>
  );
};
